import { useEffect, useState } from "react";
import { Phone, MapPin, Camera, Truck, HeartHandshake, Check, ArrowRight } from "lucide-react";
import { CITIES, NEIGHBORHOODS, cityBySlug, cityPath } from "../lib/cities";
import EstimateFlow from "../components/EstimateFlow";
import StickyMobileBar from "../components/StickyMobileBar";

const PHONE = "617-717-9852";
const PHONE_HREF = "tel:+16177179852";
const SERVICES = [
  { label: "Furniture removal", href: "/services/furniture-removal" },
  { label: "Garage cleanouts", href: "/services/garage-cleanout" },
  { label: "Basement cleanouts", href: "/services/garage-cleanout" },
  { label: "Estate cleanouts", href: "/services/estate-cleanout" },
  { label: "Appliance removal", href: "/services/appliance-removal" },
  { label: "Mattress pickup", href: "/services/mattress-removal" },
  { label: "Donation runs", href: "/services/donation-pickup" },
  { label: "Same-day pickups", href: "/services/same-day-junk-removal" },
];
const STEPS = [
  { icon: Camera, title: "Snap a photo", text: "Take a quick photo of what needs to go. Our AI gives you a firm, instant quote — no in-person visit." },
  { icon: Truck, title: "We haul it", text: "A friendly, insured crew shows up on time, does all the lifting, and sweeps up after." },
  { icon: HeartHandshake, title: "Donation first", text: "Usable items go to local donation partners before anything hits the landfill." },
];

function setMeta(city) {
  document.title = `Junk Removal in ${city.name}, ${city.abbr} | milla — Instant Photo Quotes`;
  const desc = `Junk removal in ${city.name}, ${city.abbr}. Snap a photo, get a firm instant quote. Donation-first, licensed & insured. Pickups from $249. Call ${PHONE}.`;
  document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
  let canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute("href", `https://usemilla.com${cityPath(city)}`);
  const ld = document.createElement("script");
  ld.type = "application/ld+json";
  ld.id = "city-jsonld";
  ld.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Junk Removal in ${city.name}, ${city.abbr}`,
    serviceType: "Junk Removal",
    provider: { "@id": "https://usemilla.com/#business", "@type": "LocalBusiness", name: "milla", telephone: "+16177179852", url: "https://usemilla.com/" },
    areaServed: { "@type": "City", name: city.name, containedInPlace: { "@type": "State", name: city.state } },
    url: `https://usemilla.com${cityPath(city)}`,
  });
  document.head.appendChild(ld);
  return () => document.getElementById("city-jsonld")?.remove();
}

export default function CityPage() {
  const slug = window.location.pathname.replace("/junk-removal/", "").replace(/\/$/, "");
  const city = cityBySlug(slug);
  const [flowOpen, setFlowOpen] = useState(false);

  useEffect(() => {
    if (city) return setMeta(city);
    window.location.replace("/");
  }, [city]);

  if (!city) return null;
  const all = [...CITIES, ...NEIGHBORHOODS];
  const nearby = all.filter((c) => c.abbr === city.abbr && c.slug !== city.slug);
  const parent = city.parentSlug ? cityBySlug(city.parentSlug) : null;

  return (
    <div className="App grain min-h-screen bg-canvas" data-testid="city-page">
      <header className="px-5 pt-5 sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between rounded-full bg-white/80 px-6 py-3.5 ring-1 ring-charcoal/5 backdrop-blur">
          <a href="/" data-testid="city-logo-link" className="font-logo font-semibold text-3xl lowercase text-violet">milla</a>
          <div className="flex items-center gap-4">
            <a href={PHONE_HREF} data-testid="city-phone-link" className="hidden items-center gap-2 text-sm font-semibold text-charcoal sm:inline-flex">
              <Phone size={16} className="text-violet" /> {PHONE}
            </a>
            <button onClick={() => setFlowOpen(true)} data-testid="city-header-cta" className="rounded-full bg-violet px-6 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-violet-deep">
              Get an instant quote
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8">
        <section className="pt-16 pb-12 sm:pt-24 sm:pb-16">
          <p className="inline-flex items-center gap-2 rounded-full bg-lavender px-4 py-1.5 text-sm font-semibold text-violet">
            <MapPin size={14} /> Serving {city.name}, {city.state}
          </p>
          {parent && (
            <p className="mt-3 text-sm text-charcoal/50">
              Part of our{" "}
              <a href={cityPath(parent)} data-testid="parent-town-link" className="font-semibold text-violet underline-offset-4 hover:underline">
                {parent.name}, {parent.abbr}
              </a>{" "}
              service area
            </p>
          )}
          <h1 className="mt-6 max-w-3xl font-display text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl" data-testid="city-h1">
            Junk removal in {city.name}, {city.abbr} — get your space back.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-charcoal/60">{city.blurb}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button onClick={() => setFlowOpen(true)} data-testid="city-hero-cta" className="inline-flex items-center gap-2 rounded-full bg-violet px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-violet-deep hover:-translate-y-0.5">
              Get an instant photo quote <ArrowRight size={18} />
            </button>
            <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-charcoal ring-1 ring-charcoal/10 transition-colors hover:bg-lavender hover:text-violet">
              <Phone size={18} /> {PHONE}
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-charcoal/55">
            {["Licensed & insured", "Firm quotes from a photo", "Pickups from $249", "Donation-first"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><Check size={15} className="text-violet" /> {t}</span>
            ))}
          </div>
        </section>

        <section className="grid gap-5 pb-16 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.title} className="rounded-[2rem] bg-white/70 p-8 ring-1 ring-charcoal/5">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-lavender text-violet"><s.icon size={22} /></span>
              <h2 className="mt-5 font-display text-lg text-charcoal">{s.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-charcoal/60">{s.text}</p>
            </div>
          ))}
        </section>

        <section className="pb-16">
          <h2 className="font-display text-lg text-charcoal">What we pick up in {city.name}</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <a key={s.label} href={s.href} className="rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-charcoal/70 ring-1 ring-charcoal/5 transition-colors hover:bg-lavender hover:text-violet">{s.label}</a>
            ))}
          </div>
        </section>

        <section className="pb-16">
          <div className="rounded-[2.5rem] bg-charcoal px-8 py-12 text-canvas sm:px-14">
            <h2 className="font-display text-2xl text-white sm:text-3xl">Ready to clear it out?</h2>
            <p className="mt-3 max-w-xl text-canvas/60">Snap a photo of your junk and get a firm price in under a minute. {city.name} pickups are typically scheduled within the week.</p>
            <button onClick={() => setFlowOpen(true)} data-testid="city-bottom-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-violet px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-violet-deep">
              Get an instant quote <ArrowRight size={16} />
            </button>
          </div>
        </section>

        <section className="pb-16">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-charcoal/40">Also serving nearby in {city.state}</h2>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
            {nearby.map((c) => (
              <a key={c.slug} href={cityPath(c)} className="text-sm text-charcoal/60 underline-offset-4 transition-colors hover:text-violet hover:underline">
                Junk removal in {c.name}, {c.abbr}
              </a>
            ))}
            <a href="/" className="text-sm font-semibold text-violet underline-offset-4 hover:underline">All service areas →</a>
          </div>
        </section>
      </main>

      <footer className="px-5 pb-8 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 border-t border-charcoal/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-charcoal/45">© {new Date().getFullYear()} milla. Get your space back.</p>
          <a href={PHONE_HREF} className="text-sm text-charcoal/60 hover:text-violet">Call {PHONE}</a>
        </div>
      </footer>

      <StickyMobileBar onStart={() => setFlowOpen(true)} hidden={flowOpen} />
      <EstimateFlow open={flowOpen} onClose={() => setFlowOpen(false)} />
    </div>
  );
}
